import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import ScreenBuilder from "../screens/screen-builder";
import { useScreenWidth } from "../hooks/useScreenWidth";

function Dialog({
  appData,
  components: {
    DialogBody: Body,
    DialogHeaderContainer: HeaderContainer,
    DialogContent: Content,
    DialogOverlay: Overlay,
    DialogFooter: Footer,
    DialogHeader: Header,
    DialogSubHeader: SubHeader,
    DialogCloseButton: CloseButton,
    HYDialogCloseButton: HYCloseButton,
    Button
  },
  components,
  type,
  children,
  active,
  title,
  label,
  actions = [],
  fullscreen = false,
  modalScreen = false,
  dialogOverlay = true,
  onClose,
  withClose,
  formRef,
  dialogClass = null,
  headerClass = null,
  footerClass,
  screen = null,
  hyCloseButton = false,
  meta = {},
  show_icon_warning,
  dialogScreen = false,
  overlayClass,
  getModalPosition = null,
  dismissTime = null
}) {
  const [customScreen, setCustomScreen] = useState(null);
  const [dialogTitle, addDialogTitle] = useState("");
  const [overlayPadding, setOverlayPadding] = useState("");
  const screenWidth = useScreenWidth();
  const [hidden, setHidden] = useState(false);

  const ANIMATION_TIME = 10;

  useEffect(() => {
    if (dismissTime) {
      const timeDismissId = setTimeout(() => {
        setHidden(true);
      }, dismissTime);
      const timeCloseId = setTimeout(() => {
        onClose();
      }, dismissTime + ANIMATION_TIME);
      return () => {
        clearTimeout(timeDismissId);
        clearTimeout(timeCloseId);
      };
    }
  });

  useEffect(() => {
    setHidden(false);
  }, [dismissTime]);

  useEffect(() => {
    addDialogTitle(title);
  }, [title]);

  useEffect(() => {
    if (!fullscreen) {
      if (active && dialogOverlay) {
        const paddingRight = `${
          window.innerWidth - document.body.clientWidth
        }px`;
        document.body.style.paddingRight = paddingRight;
        document.body.style.overflow = "hidden";
        setOverlayPadding(paddingRight);
      } else {
        document.body.style.paddingRight = "0";
        document.body.style.overflow = "auto";
        setOverlayPadding("");
      }
    }
  }, [active]);

  useEffect(() => {
    if (screen) {
      if (
        (screen.params || "").includes("TDScreenOptionProfileScreen") ||
        screen.name === "CareTeamProfileDetailScreen"
      ) {
        addDialogTitle(appData.providerDetailData.name);
      } else if (screen.name === "ConsultationPhotoModal") {
        addDialogTitle(appData?.settings?.translations.add_file_or_photo);
      } else if (screen.title) {
        addDialogTitle(screen.title);
      }

      setCustomScreen({ ...screen, type: "modal_screen", title: "" });
    } else {
      if (!title) {
        addDialogTitle("");
      }
      setCustomScreen(null);
    }
  }, [screen]);

  useEffect(() => {
    if (active && appData && !screen) {
      appData.initializeDialog();
    }
  }, [screen, active]);

  const getSortedActions = () =>
    [...actions].sort((a, b) => {
      const isASecondary = a.className?.includes("secondary");
      const isBSecondary = b.className?.includes("secondary");

      if (isASecondary && !isBSecondary) {
        return 1;
      } else if (isBSecondary && !isASecondary) {
        return -1;
      }
      return 0;
    });

  // We change buttons order in the DOM so that we could have proper focus order
  const actionsToRender = screenWidth < 576 ? getSortedActions() : actions;
  const isDfd = appData.properLink;

  const dialogBody = active ? (
    <Overlay
      fullscreen={fullscreen}
      meta={meta}
      paddingRight={overlayPadding}
      className={overlayClass}
      dialogOverlay={dialogOverlay}
      show_icon_warning={show_icon_warning}
    >
      <Body
        getModalPosition={getModalPosition}
        fullscreen={fullscreen}
        type={type}
        bodyClass={`${modalScreen ? "modal-screen" : ""} ${
          dialogScreen ? "dialog-screen" : ""
        } ${dialogClass}${hidden ? " hidden" : ""}`}
      >
        {((withClose && !dialogScreen) || dialogTitle) && !isDfd && (
          <HeaderContainer headerClass={headerClass}>
            {dialogTitle ? (
              <Header headerClass={headerClass}>{dialogTitle}</Header>
            ) : (
              <h1>&nbsp;</h1>
            )}
            {hyCloseButton && <HYCloseButton onClick={onClose} />}
            {withClose && (
              <CloseButton
                onClick={onClose}
                ariaLabel={appData?.settings?.translations.close_modal?.replace(
                  "%dialog_title%",
                  dialogTitle || ""
                )}
              />
            )}
          </HeaderContainer>
        )}
        {dialogScreen && withClose && !isDfd && (
          <CloseButton onClick={onClose} />
        )}
        {(label || children || screen) && (
          <Content contentClass={screen && screen.name}>
            {withClose && isDfd && (
              <CloseButton isDfd={isDfd} onClick={onClose} />
            )}
            {label && <SubHeader>{label}</SubHeader>}
            {children}
            {customScreen && (
              <ScreenBuilder
                appData={appData}
                components={components}
                screen={customScreen}
                isRoot
              />
            )}
          </Content>
        )}
        {!!actions.length && (
          <Footer footerClass={footerClass}>
            {actionsToRender.map(
              (
                { label: theLabel, onClick, name, className = "", ariaLabel },
                index
              ) => (
                <Button
                  name={name || `modal-action-${index}`}
                  key={theLabel}
                  label={theLabel}
                  onClick={onClick}
                  className={className}
                  ariaLabel={ariaLabel}
                />
              )
            )}
          </Footer>
        )}
      </Body>
    </Overlay>
  ) : null;

  const body = formRef?.current || document.getElementsByTagName("body")[0];
  const webSdkScreen = body.getElementsByClassName("screen-web-sdk")[0];
  if (webSdkScreen) {
    // hide/unhide screen content whenever we show a full-screen dialog
    webSdkScreen.style.display = active && fullscreen ? "none" : "block";
  }
  return ReactDOM.createPortal(dialogBody, body);
}

Dialog.propTypes = {
  active: PropTypes.bool.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      onClick: PropTypes.func
    })
  ),
  type: PropTypes.string,
  title: PropTypes.string,
  label: PropTypes.string,
  children: PropTypes.node,
  fullscreen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  withClose: PropTypes.bool,
  getModalPosition: PropTypes.func,
  show_icon_warning: PropTypes.bool,
  components: PropTypes.shape({
    DialogBody: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    DialogHeaderContainer: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func
    ]),
    DialogContent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    DialogOverlay: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    DialogFooter: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    DialogHeader: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    DialogSubHeader: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    DialogCloseButton: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    HYDialogCloseButton: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func
    ]),
    Button: PropTypes.oneOfType([PropTypes.element, PropTypes.func])
  }).isRequired
};

export default Dialog;
